import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Tooltip as AntDToolTip,
  Button,
  Col,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import AddCreditorTagModal from "features/tags/components/addCreditorTagModal";
import { useDeleteCreditorTagMutation, useFetchCreditorTagsQuery } from "features/tags/tagsAPI";
import { useRef, useState } from "react";
import styled from "styled-components";
import { getColumnSearchProps } from "common/antd-utils";

const StyledTable = styled(Table)`
  cursor: pointer;
`;

export default function CreditorTagTable() {
  const { data: creditorTags, isLoading } = useFetchCreditorTagsQuery();
  const [deleteCreditorTag] = useDeleteCreditorTagMutation();
  const [isModalVisible, setModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const {
    data: {
      [PERMISSIONS.CREDITOR_TAG__GET]: isCreditorTagGetAuthorized,
      [PERMISSIONS.CREDITOR_TAG__CREATE]: isCreditorTagCreateAuthorized,
      [PERMISSIONS.CREDITOR_TAG__UPDATE]: isCreditorTagUpdateAuthorized,
      [PERMISSIONS.CREDITOR_TAG__DELETE]: isCreditorTagDeleteAuthorized,
    },
  } = useAuthorizations();

  const onNewCreditorTag = async () => {
    setModalVisible(false);
  };

  const handleDelete = async (record) => {
    const result = await deleteCreditorTag({ id: record.id });
    if ("data" in result) {
      message.success("Deleted client tag successfully.");
    }
    if ("error" in result) {
      message.error("Unable to delete client tag.");
    }
  };

  const onEdit = (record) => {
    setModalVisible(true);
    setActiveRecord(record);
  };

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleReset = (clearFilters) => {
    clearFilters();
  };
  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text, record) => (
        <Space size="middle">
          {text}
          {record.isSystem && <Tag color="red">SYSTEM</Tag>}
        </Space>
      ),
      ...getColumnSearchProps("name", searchInput, handleSearch, handleReset),
      // @ts-ignore
      filteredValue: filteredInfo?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Notes",
      dataIndex: "notes",
    },
    {
      title: "Actions",
      key: "actions",
      hidden: !isCreditorTagUpdateAuthorized && !isCreditorTagDeleteAuthorized,
      render: (text, record) => (
        <Space size="middle">
          {isCreditorTagUpdateAuthorized && !record.isSystem && (
            <AntDToolTip placement="bottom" title="Edit" key="edit">
              <EditOutlined key="edit" onClick={() => onEdit(record)} />
            </AntDToolTip>
          )}
          {isCreditorTagDeleteAuthorized && !record.isSystem && (
            <Popconfirm
              placement="topLeft"
              okText="Yes"
              title="Are you sure you want to delete this client tag?"
              onConfirm={() => handleDelete(record)}
            >
              <DeleteOutlined key="delete-creditor-tag" />
            </Popconfirm>
          )}
        </Space>
      ),
      fixed: "right",
    },
  ].filter((column) => !column.hidden);

  return isCreditorTagGetAuthorized ? (
    <Col>
      <Row align="middle">
        <h4>Client</h4>
        {isCreditorTagCreateAuthorized && (
          <Button onClick={() => setModalVisible(true)} type="link" icon={<PlusOutlined />}>
            Add Client Tag
          </Button>
        )}
      </Row>
      <StyledTable
        loading={isLoading}
        bordered
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={columns}
        dataSource={creditorTags}
        onChange={handleChange}
      />
      {isModalVisible && (
        <AddCreditorTagModal
          title={activeRecord ? "Edit Client Tag" : "Add Client Tag"}
          open
          onOk={onNewCreditorTag}
          onCancel={() => {
            setModalVisible(false);
            setActiveRecord(null);
          }}
          creditorTag={activeRecord}
        />
      )}
    </Col>
  ) : null;
}
